import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { WorkArea } from '@xsolla/ui-core';

import { getUser, getUserGroups, getUserSocialProfile } from '../../../api';
import { Error } from '../../../api/setup/getApiError';
import { Group, User, UserSocialProfile } from '../../../api/users/types';
import { UserSettingsForm } from '../../../components';
import { Preloader, ToDashboardError } from '../../../components/ui';
import { useAppSettings, useCurrentProject } from '../../../contexts/settings';

type RouteParams = {
  userId: string;
  projectId: string;
};

const UserSettings = ({ match }: RouteComponentProps): JSX.Element | null => {
  const { projectId, userId } = match.params as RouteParams;
  const { hideTabs, showTabs } = useAppSettings();
  const { currentProject, currentProjectFetching } = useCurrentProject(projectId);
  const [userSocialProfile, setUserSocialProfile] = useState<UserSocialProfile[]>([]);
  const [userSettings, setUserSettings] = useState<User>();
  const [groups, setGroups] = useState<Group[]>([]);
  const [fetching, setFetching] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const onSocialProfileRemoved = useCallback((providerName: string) => {
    setUserSocialProfile((prev) =>
      prev.filter((profile) => profile.provider_name !== providerName)
    );
  }, []);

  useEffect(() => {
    hideTabs();

    return () => showTabs();
  }, []);

  useEffect(() => {
    setFetching(true);
    Promise.all([
      getUser(projectId, userId),
      getUserGroups(projectId),
      getUserSocialProfile(projectId, userId)
    ])
      .then(([userResp, groupResp, socialProfileResp]) => {
        setUserSettings(userResp);
        setGroups(groupResp);
        setUserSocialProfile(socialProfileResp);
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => setFetching(false));
  }, []);

  if (fetching || currentProjectFetching) {
    return (
      <WorkArea>
        <Preloader />
      </WorkArea>
    );
  }

  if (error) {
    return <ToDashboardError error={error} pageName="user-settings_open" />;
  }

  if (userSettings && currentProject) {
    return (
      <WorkArea>
        <UserSettingsForm
          groups={groups}
          userData={userSettings}
          currentProject={currentProject}
          userSocialProfile={userSocialProfile}
          onSocialProfileRemoved={onSocialProfileRemoved}
        />
      </WorkArea>
    );
  }

  return null;
};

export default withRouter(UserSettings);
